import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Service.css';

const services = [
    {
        title: 'Visningsstädning',
        description:
            'Skinande rena hem med miljövänliga produkter och städare som bryr sig om detaljerna.',
        details:
            'Skräddarsydda checklistor för varje rum, inklusive fönsterputs och golvvård. Vi samordnar med mäklare och återkommer kostnadsfritt om något behöver åtgärdas.',
        audience: 'Privatpersoner (RUT)',
        badge: 'SIGNATUR',
        icon: '🏡',
    },
    {
        title: 'Kontorsstädning',
        description:
            'Skapa en inspirerande arbetsplats med flexibla upplägg - dagligen, veckovis eller månatligt.',
        details:
            'Skräddarsytt för företag, coworking och butiker. Checklistor för höga hygienkrav, fönsterputs och golvvård som tillval. Fakturering med tydliga SLA:er.',
        audience: 'Företag',
        badge: 'FLEX',
        icon: '💼',
    },
    {
        title: 'Flyttstädning',
        description:
            'Vi lämnar inget åt slumpen när du byter adress - godkända protokoll och fönsterputs ingår.',
        details:
            'Fullständig genomgång av kök, badrum och fönster. RUT-avdrag för privatpersoner. Vi samordnar med besiktning och återkommer kostnadsfritt om något behöver åtgärdas.',
        audience: 'Privatpersoner (RUT)',
        badge: 'FULL SERVICE',
        icon: '🧳',
    },
    {
        title: 'Storstädning',
        description:
            'En djupgående rengöring som återställer glansen i varje hörn - perfekt för vårstädning eller före/efter större evenemang.',
        details:
            'Grundlig rengöring av alla ytor, inklusive svåråtkomliga områden. Vi använder miljövänliga produkter och anpassar tjänsten efter dina behov.',
        audience: 'Privatpersoner (RUT)',
        badge: 'SIGNATUR',
        icon: '🧹',
    }
   
];

function Services() {
    const [selected, setSelected] = useState(null);
    const modalRef = useRef(null);
    const lastFocusedRef = useRef(null);

    const closeModal = () => {
        setSelected(null);
        const target = lastFocusedRef.current;
        if (target && typeof target.focus === 'function') {
            setTimeout(() => target.focus(), 0);
        }
    };

    const openModal = (service, triggerEl) => {
        lastFocusedRef.current = triggerEl;
        setSelected(service);
    };

    useEffect(() => {
        if (!selected) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                closeModal();
                return;
            }

            if (event.key === 'Tab' && modalRef.current) {
                const focusables = modalRef.current.querySelectorAll(
                    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
                );
                if (!focusables.length) return;

                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement;

                if (event.shiftKey && active === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && active === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        const lockBodyScroll = () => {
            const prevBodyOverflow = document.body.style.overflow;
            const prevBodyTouch = document.body.style.touchAction;
            const prevDocOverflow = document.documentElement.style.overflow;
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
            document.documentElement.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prevBodyOverflow;
                document.body.style.touchAction = prevBodyTouch;
                document.documentElement.style.overflow = prevDocOverflow;
            };
        };

        const releaseBodyScroll = lockBodyScroll();
        document.addEventListener('keydown', handleKeyDown);
        const focusTimer = setTimeout(() => {
            modalRef.current?.focus();
        }, 50);

        return () => {
            clearTimeout(focusTimer);
            document.removeEventListener('keydown', handleKeyDown);
            releaseBodyScroll();
        };
    }, [selected]);

    return (
        <section className="services-section" id="services" aria-labelledby="services-title">
            <div className="services-intro">
                
                <h2 id="services-title">Förstklassig städning för varje behov</h2>
                <p>
                    Rentstäd paketerar premiumkvalitet, pålitlighet och lyhörd service i varje uppdrag -
                    från mindre hem till omfattande kommersiella miljöer.
                </p>
            </div>

            <div className="services-grid">
                {services.map((service) => (
                    <article className="service-card" key={service.title}>
                        <div className="service-card-top">
                            <span className="service-icon" aria-hidden="true">
                                {service.icon}
                            </span>
                            <span className="service-badge">{service.badge}</span>
                        </div>
                        <h3>{service.title}</h3>
                        <p>{service.description}</p>
                        <button
                            className="service-link"
                            type="button"
                            onClick={(event) => openModal(service, event.currentTarget)}
                            aria-label={`Läs mer om ${service.title}`}
                        >
                            Läs mer
                            <span aria-hidden="true">→</span>
                        </button>
                    </article>
                ))}
            </div>

            <div className="services-cta-band">
                <div>
                    <h3>Redo att uppleva Rentstäds precision?</h3>
                    <p>
                        Låt oss skräddarsy ett upplägg som passar din vardag. Transparent prissättning och tydliga
                        leveranser - alltid.
                    </p>
                </div>
                <div className="services-cta-buttons">
                    <Link className="services-cta primary" to="/calculator">Räkna ut ditt pris</Link>
                </div>
            </div>

            {selected && (
                <div
                    className="service-modal is-visible"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="service-modal-title"
                    aria-describedby="service-modal-desc"
                    onClick={closeModal}
                >
                    <div
                        className="service-modal-card"
                        ref={modalRef}
                        tabIndex={-1}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button className="service-modal-close" onClick={closeModal} aria-label="Stäng">
                            ×
                        </button>
                        <div className="service-modal-header">
                            <div className="service-modal-icon" aria-hidden="true">{selected.icon}</div>
                            <div>
                                <h3 id="service-modal-title">{selected.title}</h3>
                                <p className="service-modal-audience">{selected.audience}</p>
                            </div>
                        </div>
                        <div className="service-modal-content" id="service-modal-desc">
                            <p className="service-modal-details">{selected.details}</p>
                            {selected.audience.includes('RUT') && (
                                <div className="service-modal-rut">RUT-avdrag: upp till 50% arbetskostnad för privatpersoner.</div>
                            )}
                            <div className="service-modal-actions">
                                <Link className="services-cta primary" to="/calculator" onClick={closeModal}>
                                    Räkna ut ditt pris
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

export default Services;